/**
 * Módulo 7 — seguimento da criança exposta à sífilis na puericultura.
 *  - D-041: o calendário do D-003 vira itens de agenda (coletas e consultas), cada um com a sua
 *    situação; o exame ou a consulta registrados "realizam" o item mais próximo do mesmo tipo;
 *  - D-044: sugestão de encerramento (o médico confirma).
 */
import { mesesCompletos, somarMeses } from '../datas/calendario';
import { diasEntre, formatarDataBR } from '../datas/gestacao';
import type { RegrasSeguimento } from './regras';
import { alertasSeguimento, calendarioSeguimento, type ColetaSeguimento } from './seguimento';
import { reagente } from './titulos';

// ---------------------------------------------------------------- calendário (D-041)
/** Os mesmos valores do enum `tipo_agendamento_enum` do banco. */
export const TIPOS_ITEM_SEGUIMENTO = [
  'coleta_vdrl',
  'coleta_treponemico',
  'consulta_puericultura',
] as const;
export type TipoItemSeguimento = (typeof TIPOS_ITEM_SEGUIMENTO)[number];

export const ROTULO_ITEM_SEGUIMENTO: Record<TipoItemSeguimento, string> = {
  coleta_vdrl: 'Teste não treponêmico (VDRL/RPR)',
  coleta_treponemico: 'Teste treponêmico',
  consulta_puericultura: 'Consulta de puericultura',
};

export interface ItemCalendario {
  tipo: TipoItemSeguimento;
  idadeMeses: number;
  dataPrevista: string;
}

/** Itens de agenda gerados ao iniciar o seguimento, em ordem de data. */
export function itensCalendario(
  regras: RegrasSeguimento,
  dataNascimento: string,
): ItemCalendario[] {
  const c = calendarioSeguimento(regras, dataNascimento);
  const itens: ItemCalendario[] = [
    ...c.coletasNaoTreponemico.map((x) => ({
      tipo: 'coleta_vdrl' as const,
      idadeMeses: x.meses,
      dataPrevista: x.data,
    })),
    {
      tipo: 'coleta_treponemico',
      idadeMeses: regras.teste_treponemico_a_partir_meses,
      dataPrevista: c.treponemicoAPartirDe,
    },
    ...c.consultas.map((x) => ({
      tipo: 'consulta_puericultura' as const,
      idadeMeses: x.meses,
      dataPrevista: x.data,
    })),
  ];
  const ordem = (t: TipoItemSeguimento) => TIPOS_ITEM_SEGUIMENTO.indexOf(t);
  return itens.sort(
    (a, b) => a.dataPrevista.localeCompare(b.dataPrevista) || ordem(a.tipo) - ordem(b.tipo),
  );
}

export type SituacaoItem = 'realizado' | 'cancelado' | 'atrasado' | 'proximo' | 'previsto';

export const ROTULO_SITUACAO_ITEM: Record<SituacaoItem, string> = {
  realizado: 'Realizado',
  cancelado: 'Cancelado',
  atrasado: 'Atrasado',
  proximo: 'Próximo',
  previsto: 'Previsto',
};

/** Dias de antecedência em que um item previsto passa a aparecer como "próximo". */
export const DIAS_ITEM_PROXIMO = 15;

/** Situação de um item da agenda hoje: passou da data sem registro → atrasado. */
export function situacaoItem(
  item: { dataPrevista: string; status: string },
  hoje: string,
): SituacaoItem {
  if (item.status === 'realizado') return 'realizado';
  if (item.status === 'cancelado') return 'cancelado';
  if (item.dataPrevista < hoje) return 'atrasado';
  return diasEntre(hoje, item.dataPrevista) <= DIAS_ITEM_PROXIMO ? 'proximo' : 'previsto';
}

/** Tipo de item que um exame realiza (null = o exame não faz parte do calendário). */
export function tipoItemDoExame(tipoExame: string): TipoItemSeguimento | null {
  if (['vdrl', 'rpr'].includes(tipoExame)) return 'coleta_vdrl';
  // O teste rápido de sífilis é treponêmico
  if (['teste_treponemico', 'ftabs', 'tpha', 'sifilis'].includes(tipoExame)) {
    return 'coleta_treponemico';
  }
  return null;
}

/**
 * Exame como coleta da série de títulos (D-003): só sífilis, com resultado; VDRL/RPR reagente
 * precisa do título. null = não entra na série.
 */
export function coletaDoExame(e: {
  tipo_exame: string;
  resultado: string;
  valor_titulacao: string | null;
  data_coleta: string | null;
}): ColetaSeguimento | null {
  const tipo = tipoItemDoExame(e.tipo_exame);
  if (!tipo || !e.data_coleta) return null;
  if (e.resultado !== 'reagente' && e.resultado !== 'nao_reagente') return null;
  if (tipo === 'coleta_treponemico') {
    return { data: e.data_coleta, tipo: 'treponemico', resultado: e.resultado };
  }
  const titulo =
    e.valor_titulacao && e.valor_titulacao !== 'nao_aplicavel' ? e.valor_titulacao : null;
  if (e.resultado === 'reagente' && !titulo) return null;
  return {
    data: e.data_coleta,
    tipo: 'nao_treponemico',
    resultado: e.resultado === 'reagente' ? (titulo as string) : 'nao_reagente',
  };
}

/**
 * Item em aberto que um registro (exame ou consulta) realiza: o do mesmo tipo com a data
 * prevista mais próxima da data do registro (no empate, o mais antigo).
 */
export function itemCorrespondente<
  T extends { tipo: string; status: string; dataPrevista: string },
>(itens: T[], registro: { tipo: TipoItemSeguimento; data: string }): T | null {
  const abertos = itens.filter(
    (i) => i.tipo === registro.tipo && i.status !== 'realizado' && i.status !== 'cancelado',
  );
  let melhor: T | null = null;
  let menor = Infinity;
  for (const i of [...abertos].sort((a, b) => a.dataPrevista.localeCompare(b.dataPrevista))) {
    const distancia = Math.abs(diasEntre(i.dataPrevista, registro.data));
    if (distancia < menor) {
      melhor = i;
      menor = distancia;
    }
  }
  return melhor;
}

// ---------------------------------------------------------------- encerramento (D-044)
/** Os mesmos valores do enum `classificacao_seguimento_enum` do banco. */
export const CLASSIFICACOES_SEGUIMENTO = [
  'sifilis_congenita_descartada',
  'sifilis_congenita_confirmada',
  'perda_seguimento',
  'transferencia',
] as const;
export type ClassificacaoSeguimento = (typeof CLASSIFICACOES_SEGUIMENTO)[number];

export const ROTULO_CLASSIFICACAO_SEGUIMENTO: Record<ClassificacaoSeguimento, string> = {
  sifilis_congenita_descartada: 'Sífilis congênita descartada (criança exposta sem infecção)',
  sifilis_congenita_confirmada: 'Sífilis congênita confirmada',
  perda_seguimento: 'Perda de seguimento',
  transferencia: 'Transferência',
};

export interface EntradaEncerramento {
  dataNascimento: string;
  hoje: string;
  coletas: ColetaSeguimento[];
  tratadaAdequadamente: boolean;
  /** Datas de consultas registradas (contam para a perda de seguimento). */
  consultas: string[];
  /** Mudou para fora da rede (informado pelo profissional). */
  transferida?: boolean;
}

export interface SugestaoEncerramento {
  classificacao: ClassificacaoSeguimento | null;
  /** O que ainda falta para sugerir o encerramento. */
  falta: string | null;
  explicacao: string[];
}

export function sugerirEncerramento(
  regras: RegrasSeguimento,
  e: EntradaEncerramento,
): SugestaoEncerramento {
  if (e.transferida) {
    return {
      classificacao: 'transferencia',
      falta: null,
      explicacao: ['A criança mudou para fora da rede: transferência.'],
    };
  }

  const { alertas } = alertasSeguimento(regras, {
    dataNascimento: e.dataNascimento,
    coletas: e.coletas,
    tratadaAdequadamente: e.tratadaAdequadamente,
  });
  if (alertas.length) {
    return {
      classificacao: 'sifilis_congenita_confirmada',
      falta: null,
      explicacao: [...alertas.map((a) => `⚠ ${a.mensagem}`), '→ Sífilis congênita confirmada.'],
    };
  }

  // Descartada: treponêmico não reagente na idade do protocolo e último não treponêmico não reagente
  const ordenadas = [...e.coletas].sort((a, b) => a.data.localeCompare(b.data));
  const idadeTrep = regras.teste_treponemico_a_partir_meses;
  const treponemico = ordenadas
    .filter((c) => c.tipo === 'treponemico')
    .filter((c) => mesesCompletos(e.dataNascimento, c.data) >= idadeTrep)
    .at(-1);
  const ultimoNaoTrep = ordenadas.filter((c) => c.tipo === 'nao_treponemico').at(-1);
  if (treponemico && !reagente(treponemico.resultado)) {
    if (!ultimoNaoTrep || reagente(ultimoNaoTrep.resultado)) {
      return {
        classificacao: null,
        falta: 'teste não treponêmico não reagente',
        explicacao: [
          `Treponêmico não reagente em ${formatarDataBR(treponemico.data)}.`,
          ultimoNaoTrep
            ? `Último teste não treponêmico ainda reagente (${ultimoNaoTrep.resultado}, ${formatarDataBR(ultimoNaoTrep.data)}).`
            : 'Nenhum teste não treponêmico registrado.',
        ],
      };
    }
    return {
      classificacao: 'sifilis_congenita_descartada',
      falta: null,
      explicacao: [
        `Teste não treponêmico não reagente em ${formatarDataBR(ultimoNaoTrep.data)}.`,
        `Treponêmico não reagente em ${formatarDataBR(treponemico.data)} (${mesesCompletos(e.dataNascimento, treponemico.data)} meses), sem alertas na série.`,
        '→ Sífilis congênita descartada.',
      ],
    };
  }

  // Perda de seguimento: nenhum registro há muito tempo (❓ proposta)
  const registros = [...e.coletas.map((c) => c.data), ...e.consultas].sort();
  const desde = registros.at(-1) ?? e.dataNascimento;
  const limite = somarMeses(desde, regras.perda_seguimento_meses);
  if (e.hoje >= limite) {
    return {
      classificacao: 'perda_seguimento',
      falta: null,
      explicacao: [
        `${registros.length ? 'Último registro' : 'Nascimento'} em ${formatarDataBR(desde)}: ${regras.perda_seguimento_meses} meses ou mais sem coleta nem consulta.`,
        '→ Perda de seguimento (confirme a busca ativa antes de encerrar).',
      ],
    };
  }

  const aPartirDe = somarMeses(e.dataNascimento, idadeTrep);
  return {
    classificacao: null,
    falta: `teste treponêmico a partir dos ${idadeTrep} meses (${formatarDataBR(aPartirDe)})`,
    explicacao: [
      `Seguimento em andamento: falta o teste treponêmico a partir de ${formatarDataBR(aPartirDe)}.`,
    ],
  };
}
